// The words of the Overview's "Get set up" checklist. Which steps there are, in what order, and when
// each one is done are decided on the server (src/onboarding/checklist.ts, GET /setup/checklist), which
// the MCP server's get_setup_status answers with too; this file only words a step from that answer.
// The server imports it for its own English labels, so the two never drift. Everything here is
// English, translated where it is rendered, as in booking-moves.js; test/setup-steps.test.ts holds
// every line to a Hebrew entry.

export const STEP_LABELS = {
  setup: "Let the AI write your agent from a few questions",
  services: "Add your services, durations and prices",
  knowledge: "Teach the agent your FAQs and policies",
  channel: "Connect WhatsApp, or share your Chat Link",
};

export const CALENDAR_STEP = {
  connect: "Connect your Google or Outlook calendar",
  reconnect: "Reconnect your calendar: it stopped syncing",
  link: "Calendar connected, but no staff member is linked to it yet. Pick a calendar in Services & staff.",
};

/** The calendar line each state of the calendar step shows; once ready it reads as the task it was. */
export const CALENDAR_STATE_LABEL = {
  not_connected: "connect",
  needs_reconnect: "reconnect",
  no_staff_linked: "link",
  ready: "connect",
};

/** The AI agent page's labels for the settings fields the checklist can name (AGENT_TEXT_FIELDS). */
export const AGENT_FIELD_LABELS = {
  agent_name: "Agent name",
  business_description: "About the business",
  tone: "Tone of voice",
  instructions: "Sales script and policies",
  handoff_rules: "Also hand off to a person when…",
  followup_message: "Follow-up message",
  review_message: "Review message",
  rebook_message: "Rebook message",
  nps_message: "Score question",
};

export const PLACEHOLDER_STEP = {
  done: "Replace the [bracketed] placeholders in the agent's knowledge and settings",
  todo: "Replace the [bracketed] placeholders the agent would read as facts: {names}",
};

/** A knowledge title can be Hebrew or English: isolated, it keeps its own direction inside the row. */
const isolate = (text) => `⁨${text}⁩`;

/** How many entries the row names before "and N more". */
export const PLACEHOLDER_NAMES_SHOWN = 3;

/**
 * The placeholder row: done when `entries` (knowledge entries, { id, title }) and `fields` (agent
 * settings) are both empty, else naming them. `tr` translates a label (a plain fill in tests and on
 * the server).
 */
export function placeholderLabel(entries, fields, tr = fill) {
  const names = [...entries.map((k) => isolate(k.title)), ...fields.map((f) => tr(AGENT_FIELD_LABELS[f] ?? f))];
  if (!names.length) return tr(PLACEHOLDER_STEP.done);
  const shown = names.slice(0, PLACEHOLDER_NAMES_SHOWN).join(", ");
  const more = names.length - PLACEHOLDER_NAMES_SHOWN;
  return tr(PLACEHOLDER_STEP.todo, { names: more > 0 ? `${shown} ${tr("and {n} more", { n: more })}` : shown });
}

/** The label of one step of GET /setup/checklist, in the language `tr` translates to. */
export function stepLabel(step, tr = fill) {
  if (step.id === "calendar") return tr(CALENDAR_STEP[CALENDAR_STATE_LABEL[step.state] ?? "connect"]);
  if (step.id === "placeholders") return placeholderLabel(step.entries ?? [], step.fields ?? [], tr);
  return STEP_LABELS[step.id] ? tr(STEP_LABELS[step.id]) : step.label;
}

function fill(s, vars) {
  let out = s;
  if (vars) for (const [k, v] of Object.entries(vars)) out = out.split(`{${k}}`).join(String(v ?? ""));
  return out;
}
